// Where the page's pricing comes from (PRD-1553): the version the build baked
// into the HTML — a JSON island beside the baked amounts — and the live one
// `/pricing/active.json` answers, read once on load. The public read is served
// same-origin by the Hosting rewrite (DATA-034).

import type { SitePricing } from './apply-pricing';

/** The id of the `<script type="application/json">` the build writes. */
export const SITE_PRICING_ID = 'site-pricing';

export const ACTIVE_PRICING_PATH = '/pricing/active.json';

const isAmount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

/** A pricing answer, or `null` for anything else — a wrong body prices nothing. */
export function readSitePricing(value: unknown): SitePricing | null {
  if (!value || typeof value !== 'object') return null;
  const { version, lines } = value as Record<string, unknown>;
  if (typeof version !== 'number' || !Number.isInteger(version) || !lines || typeof lines !== 'object') return null;
  for (const entry of Object.values(lines as Record<string, unknown>)) {
    const { monthly, annual } = (entry ?? {}) as Record<string, unknown>;
    if (!isAmount(monthly) || !isAmount(annual)) return null;
  }
  return value as SitePricing;
}

/** The pricing the build baked into the page, or `null` without one. */
export function bakedPricing(document: Document): SitePricing | null {
  const island = document.getElementById(SITE_PRICING_ID);
  if (!island?.textContent) return null;
  try {
    return readSitePricing(JSON.parse(island.textContent));
  } catch {
    return null;
  }
}

type FetchLike = (input: string, init?: RequestInit) => Promise<{ ok: boolean; json(): Promise<unknown> }>;

/**
 * The live pricing, or `null` when the read fails. Silent by design: the baked
 * amounts stay in place, which is a correct page — only an older one.
 */
export async function fetchActivePricing(fetchImpl: FetchLike = fetch): Promise<SitePricing | null> {
  try {
    const response = await fetchImpl(ACTIVE_PRICING_PATH, { credentials: 'omit' });
    return response.ok ? readSitePricing(await response.json()) : null;
  } catch {
    return null;
  }
}
